import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, XIcon, SendIcon, MinimizeIcon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useWindows } from '../../contexts/WindowContext';
import { getGradientClasses } from '../../utils/helpers';
import { findBestResponse } from '../../utils/chatbotKnowledge';
interface Message {
  id: number;
  text: string;
  sender: 'user' | 'ai';
}
export function AICompanion() {
  const {
    t,
    getThemeClasses,
    theme
  } = useTheme();
  const {
    isMobile
  } = useWindows();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showIntroHint, setShowIntroHint] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: 1,
    text: "👋 Hi! I'm Mick's AI assistant. I can help you explore his portfolio and answer questions about his expertise, process, and projects. What would you like to know?",
    sender: 'ai'
  }]);
  const [input, setInput] = useState('');
  const [typingMessage, setTypingMessage] = useState<string | null>(null);
  useEffect(() => {
    const hasOpenedAbout = localStorage.getItem('hasOpenedAbout') === 'true';
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding') === 'true';
    const hasSeenAIHint = localStorage.getItem('hasSeenAIHint') === 'true';
    if (hasSeenOnboarding && hasOpenedAbout && !hasSeenAIHint) {
      setTimeout(() => {
        setShowIntroHint(true);
        localStorage.setItem('hasSeenAIHint', 'true');
        setTimeout(() => {
          setShowIntroHint(false);
        }, 8000);
      }, 2000);
    }
  }, []);
  const toggleOpen = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
    setShowIntroHint(false);
  };
  const handleSend = () => {
    if (!input.trim()) return;
    const userMessage: Message = {
      id: Date.now(),
      text: input,
      sender: 'user'
    };
    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setTypingMessage('Thinking...');
    setTimeout(() => {
      const response = findBestResponse(userInput);
      setTypingMessage(null);
      const aiMessage: Message = {
        id: Date.now() + 1,
        text: response,
        sender: 'ai'
      };
      setMessages(prev => [...prev, aiMessage]);
    }, 1000);
  };
  return <>
      <motion.button className={`ai-companion-button fixed ${isMobile ? 'bottom-24' : 'bottom-20'} right-6 z-40 ${gradients.accent} ${gradients.shadow} flex items-center justify-center overflow-hidden`} initial={{
      scale: 1
    }} animate={{
      scale: showIntroHint ? [1, 1.05, 1] : 1,
      width: showIntroHint ? ['3.5rem', '16rem'] : '3.5rem',
      height: '3.5rem',
      borderRadius: showIntroHint ? ['1.75rem', '1rem'] : '1.75rem'
    }} transition={{
      duration: 0.4,
      ease: 'easeInOut'
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={toggleOpen} aria-label="Open AI Assistant" data-id="element-613">
        <AnimatePresence mode="wait" data-id="element-614">
          {showIntroHint ? <motion.div key="hint" initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="flex items-center gap-2 px-4 py-2" data-id="element-615">
              <SparklesIcon className="w-5 h-5 text-white flex-shrink-0" data-id="element-616" />
              <span className="text-white font-medium text-sm whitespace-nowrap" data-id="element-617">
                Ask me anything!
              </span>
            </motion.div> : <motion.div key="icon" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.8
        }} data-id="element-618">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-619" />
            </motion.div>}
        </AnimatePresence>
      </motion.button>
      <AnimatePresence data-id="element-620">
        {isOpen && !isMinimized && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`fixed ${isMobile ? 'bottom-28' : 'bottom-24'} right-6 z-40 w-80 md:w-96 ${themeClasses.card} border ${themeClasses.border} rounded-2xl overflow-hidden shadow-2xl`} role="dialog" aria-modal="true" aria-labelledby="ai-assistant-title" data-id="element-621">
            <div className={`p-4 border-b ${themeClasses.border} flex items-center justify-between`} data-id="element-622">
              <div className="flex items-center space-x-3" data-id="element-623">
                <div className={`w-10 h-10 rounded-full ${gradients.accent} flex items-center justify-center shadow-lg`} data-id="element-624">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-625" />
                </div>
                <div data-id="element-626">
                  <h3 id="ai-assistant-title" className={`font-semibold text-sm ${themeClasses.text}`} data-id="element-627">
                    AI Portfolio Assistant
                  </h3>
                  <p className={`text-xs ${themeClasses.text} opacity-70`} data-id="element-628">
                    Ask about Mick's work
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1" data-id="element-629">
                <button onClick={() => setIsMinimized(true)} className={`p-2 hover:bg-gray-500/10 rounded-lg transition-colors`} aria-label="Minimize chat" data-id="element-630">
                  <MinimizeIcon className={`w-4 h-4 ${themeClasses.icon}`} data-id="element-631" />
                </button>
                <button onClick={() => setIsOpen(false)} className={`p-2 hover:bg-gray-500/10 rounded-lg transition-colors`} aria-label="Close chat" data-id="element-632">
                  <XIcon className={`w-4 h-4 ${themeClasses.icon}`} data-id="element-633" />
                </button>
              </div>
            </div>
            <div className="h-80 overflow-y-auto p-4 space-y-3" data-id="element-634">
              {messages.map(message => <motion.div key={message.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-635">
                  <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${message.sender === 'user' ? `${gradients.accent} text-white shadow-md` : `${themeClasses.card} border ${themeClasses.border} ${themeClasses.text}`}`} data-id="element-636">
                    {message.text}
                  </div>
                </motion.div>)}
              {typingMessage && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex justify-start" data-id="element-637">
                  <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${themeClasses.card} border ${themeClasses.border} ${themeClasses.text}`} data-id="element-638">
                    <div className="flex items-center gap-2" data-id="element-639">
                      <div className="flex space-x-1" data-id="element-640">
                        <motion.div animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1,
                    delay: 0
                  }} className="w-2 h-2 rounded-full bg-current opacity-60" data-id="element-641" />
                        <motion.div animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1,
                    delay: 0.2
                  }} className="w-2 h-2 rounded-full bg-current opacity-60" data-id="element-642" />
                        <motion.div animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1,
                    delay: 0.4
                  }} className="w-2 h-2 rounded-full bg-current opacity-60" data-id="element-643" />
                      </div>
                      <span className="text-xs opacity-70" data-id="element-644">
                        {typingMessage}
                      </span>
                    </div>
                  </div>
                </motion.div>}
            </div>
            <div className={`p-4 border-t ${themeClasses.border}`} data-id="element-645">
              <div className="flex space-x-2" data-id="element-646">
                <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Ask about expertise, projects..." className={`flex-1 px-3 py-2 ${themeClasses.card} border ${themeClasses.border} rounded-lg ${themeClasses.text} placeholder-opacity-50 outline-none text-sm focus:border-purple-500 transition-colors`} aria-label="Message input" data-id="element-647" />
                <button onClick={handleSend} disabled={!input.trim()} className={`px-4 py-2 ${gradients.accent} rounded-lg transition-all text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-md`} aria-label="Send message" data-id="element-648">
                  <SendIcon className="w-4 h-4" data-id="element-649" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
      <AnimatePresence data-id="element-650">
        {isMinimized && <motion.div initial={{
        opacity: 0,
        x: 50
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 50
      }} className={`fixed ${isMobile ? 'bottom-28' : 'bottom-24'} right-6 z-40 ${themeClasses.card} border ${themeClasses.border} rounded-xl shadow-lg py-2 px-4 cursor-pointer ${themeClasses.cardHover} transition-all`} onClick={() => setIsMinimized(false)} data-id="element-651">
            <div className="flex items-center space-x-2" data-id="element-652">
              <div className={`w-8 h-8 rounded-full ${gradients.accent} flex items-center justify-center shadow-md`} data-id="element-653">
                <SparklesIcon className="w-4 h-4 text-white" data-id="element-654" />
              </div>
              <span className={`text-sm font-medium ${themeClasses.text}`} data-id="element-655">
                AI Assistant
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}